"use client"

import { create } from "zustand"
import type { Toast } from "@/components/ui/toast"

interface ToastStore {
  toasts: Toast[]
  /** Push or replace a toast by id (id used as upsert key). */
  showToast: (toast: Toast) => void
  /** Merge a patch into an existing toast. No-op if id is absent. */
  updateToast: (id: string, patch: Partial<Toast>) => void
  /** Remove a toast. */
  dismissToast: (id: string) => void
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  showToast: (toast) =>
    set((s) => ({
      toasts: [...s.toasts.filter((t) => t.id !== toast.id), toast],
    })),
  updateToast: (id, patch) =>
    set((s) => ({
      toasts: s.toasts.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    })),
  dismissToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))

export function getErrorMessage(error: unknown, fallback = "Something went wrong") {
  if (error && typeof error === "object") {
    const detail = (error as { detail?: unknown }).detail
    if (detail && typeof detail === "object") {
      const detailMessage = (detail as { message?: unknown }).message
      if (typeof detailMessage === "string" && detailMessage.trim()) {
        return detailMessage.trim()
      }
    }

    if (typeof detail === "string" && detail.trim()) {
      return detail.trim()
    }

    const message = (error as { message?: unknown }).message
    if (typeof message === "string" && message.trim()) {
      return message.trim()
    }
  }

  if (typeof error === "string" && error.trim()) {
    return error.trim()
  }

  return fallback
}

export function showErrorToast({
  id,
  message,
  error,
  fallback,
}: {
  id?: string
  message?: string
  error?: unknown
  fallback?: string
}) {
  const finalMessage = message || getErrorMessage(error, fallback)
  const toastId =
    id || `error-${Date.now()}-${Math.random().toString(36).slice(2)}`

  useToastStore.getState().showToast({
    id: toastId,
    message: finalMessage,
    variant: "error",
  })

  return finalMessage
}
